import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { ApolloProvider } from '@apollo/client'
import { ThemeProvider } from '@emotion/react'
import { Notification } from '@nginformatica/ngi-core'
import ConnectivityContext from '@core/context/ConnectivityContext'
import { apolloClient } from '@core/graphql'
import DesignVersionProvider from '@core/layout/design-version/DesignVersionProvider'
import {
    getAuthToken,
    getLogoutPath,
    setAuthToken,
    removeAuthToken
} from '@core/lib/cache'
import publicRoutes from '@core/routes'
import { RouterContainer } from '@core/routes/RouterContainer'
import { ThemeProviderFlipper, muiThemeOptions, theme } from 'flipper-ui/theme'
import './i18n'
import '@public/index.css'

const messageExtractor = (notification: Error | string) => {
    if (typeof notification === 'string') {
        return notification
    }

    return notification.message
}

export const authChannel = new BroadcastChannel('auth_channel')

const SESSION_REQUEST_TIMEOUT_MS = 1000

const isPublicRoute = () =>
    publicRoutes.some(route => route.path === location.pathname)

const App = () => {
    const token = getAuthToken()

    useEffect(() => {
        const handleAuthChannel = (e: MessageEvent) => {
            if (e.data.message === 'NEW_TAB') {
                const currentToken = getAuthToken()

                if (currentToken) {
                    authChannel.postMessage({
                        message: 'AUTH_TOKEN',
                        token: currentToken
                    })
                }

                return
            }

            if (e.data.message === 'AUTH_TOKEN' && !token) {
                setAuthToken(e.data.token as string)
                location.reload()
            }

            if (e.data.message === 'LOGOUT') {
                removeAuthToken()

                if (!isPublicRoute()) {
                    location.replace(getLogoutPath())
                }
            }
        }

        const redirectToLogin =
            !token && !isPublicRoute()
                ? setTimeout(
                      () => location.replace(getLogoutPath()),
                      SESSION_REQUEST_TIMEOUT_MS
                  )
                : undefined

        authChannel.postMessage({ message: 'NEW_TAB' })
        authChannel.addEventListener('message', handleAuthChannel)

        return () => {
            clearTimeout(redirectToLogin)
            authChannel.removeEventListener('message', handleAuthChannel)
        }
    }, [token])

    return (
        <ApolloProvider client={apolloClient}>
            <ThemeProvider theme={theme}>
                <ThemeProviderFlipper options={muiThemeOptions}>
                    <Notification messageExtractor={messageExtractor}>
                        <DesignVersionProvider>
                            <ConnectivityContext>
                                <RouterContainer />
                            </ConnectivityContext>
                        </DesignVersionProvider>
                    </Notification>
                </ThemeProviderFlipper>
            </ThemeProvider>
        </ApolloProvider>
    )
}

const root = createRoot(document.getElementById('root') as HTMLElement)

root.render(<App />)
